import { AnimatePresence } from 'motion/react';
import { ArrowWithShortTailLeft } from '@/icons/arrow-with-short-tail-left';
import { Button } from '@/shadcn-ui/button';
import { DrawerDialog, DrawerDialogProps } from '@/common/components/drawer-dialog';
import { useState } from 'react';
import Spacer from '@/common/components/spacer';
import StatusBanner from '@/common/components/status-banner';
import StepCreditAge, { StepCreditAgeProps } from './step-credit-age';
import StepZipcode, { StepZipcodeProps } from './step-zipcode';

interface ZipCreditAgeMultiStepDrawerDialogProps
  extends Omit<StepZipcodeProps, 'submitZipcode'>,
    Omit<
      StepCreditAgeProps,
      'onAgeChange' | 'onCreditChange' | 'zipcode' | 'isOpenedFromInventory'
    >,
    DrawerDialogProps {
  selectedZipcode?: string;
  handleOpen: () => void;
  className?: string;
  error?: string;
  alreadyQualified?: boolean;
}

export default function ZipCreditAgeMultiStepDrawerDialog({
  open,
  error,
  handleClose,
  selectedCreditScoreId,
  selectedIsOver25,
  creditScores,
  selectedZipcode,
  selectedAgeRangeId,
  ageRanges,
  alreadyQualified,
}: ZipCreditAgeMultiStepDrawerDialogProps) {
  const [zipcode, setZipcode] = useState<string | undefined>(selectedZipcode);
  const [step, setStep] = useState<'zip' | 'credit_age'>(
    selectedZipcode ? 'credit_age' : 'zip'
  );

  const submitZipcode = (zipcode: string) => {
    setZipcode(zipcode);
    setStep('credit_age');
  };

  const title = (
    <>
      <AnimatePresence>
        {step === 'credit_age' ? (
          <Button onClick={() => setStep('zip')} variant="clear" className="h-auto">
            <ArrowWithShortTailLeft />
          </Button>
        ) : (
          <Spacer variant="height16" />
        )}
      </AnimatePresence>

      <Spacer variant="height16" />

      <p className="body3-strong text-flexcar-neutral50">
        STEP {step === 'zip' ? '1' : '2'} OF 2
      </p>

      <Spacer variant="height16" />

      <span
        // Note: These classNames are intended to align with the DrawerDialog header styles
        className="font-sans font-semibold leading-7 tracking-[-0.005em] md:text-[28px] md:font-semibold md:leading-[34px] md:tracking-[-0.005em]"
      >
        {step === 'credit_age' ? 'About you' : 'Find Flexcars near you'}
      </span>
    </>
  );

  return (
    <DrawerDialog
      open={open}
      handleClose={handleClose}
      headerClassName="mt-0"
      dismissible={selectedZipcode !== undefined && selectedCreditScoreId !== undefined}
      repositionInputs={false}
      onFocusCapture={(e) => e.stopPropagation()}
      title={title}
    >
      {error ? (
        <>
          <StatusBanner
            variant="error"
            message={{
              text: error,
            }}
            shouldScrollToTop={true}
          />
          <Spacer variant="height16" />
        </>
      ) : null}

      {step === 'zip' ? (
        <StepZipcode
          selectedZipcode={zipcode}
          submitZipcode={submitZipcode}
          multiStep={true}
        />
      ) : null}

      {step === 'credit_age' && zipcode ? (
        <StepCreditAge
          selectedCreditScoreId={selectedCreditScoreId}
          creditScores={creditScores}
          selectedIsOver25={selectedIsOver25 ?? true}
          selectedAgeRangeId={selectedAgeRangeId}
          ageRanges={ageRanges}
          isOpenedFromInventory={true}
          alreadyQualified={alreadyQualified}
        />
      ) : null}
    </DrawerDialog>
  );
}
